import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Star, Package, CalendarDays, ThumbsUp, Globe, Twitter, Instagram, Facebook, Youtube, Gavel, Award, MessageCircle } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { fetchPublicSeller } from "@/lib/public-sellers";
import { resolvePublicSellerName } from "@/lib/public-name";
import { SellerBadgeStack } from "@/components/seller-badge-stack";
import { ShareProfileButton } from "@/components/share-profile-button";
import { AuctionGrid } from "@/components/auction-grid";
import { SellerReviewsSection } from "@/components/seller-reviews-section";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import type { AuctionCardData } from "@/components/auction-card";
import { useTranslation } from "@/i18n";



type SellerSeo = {
  id: string;
  username: string | null;
  display_name: string | null;
  shop_name: string | null;
  bio: string | null;
  avatar_url: string | null;
  seller_profile_enabled: boolean;
} | null;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute("/seller/$sellerId")({
  loader: async ({ params }): Promise<{ seller: SellerSeo }> => {
    try {
      const handle = params.sellerId;
      const isUuid = UUID_RE.test(handle);
      const q = supabase
        .from("public_sellers" as never)
        .select("id,username,display_name,shop_name,bio,avatar_url,seller_profile_enabled");
      const { data } = isUuid
        ? await q.eq("id", handle).maybeSingle()
        : await q.ilike("username", handle.trim().toLowerCase()).maybeSingle();
      return { seller: (data as SellerSeo) ?? null };
    } catch {
      return { seller: null };
    }
  },
  head: ({ params, loaderData }) => {
    const s = loaderData?.seller;
    const handle = s?.username ?? params.sellerId;
    const url = `https://bidnovaauctions.uk/seller/${handle}`;
    const name = resolvePublicSellerName(s ?? undefined);
    const enabled = s?.seller_profile_enabled ?? false;
    const title = `${name} | Seller on BidNova Auctions`;
    const desc =
      (s?.bio?.trim() ||
        `Browse trading cards, collectibles and active auctions from ${name} on BidNova Auctions.`)
        .replace(/\s+/g, " ")
        .slice(0, 155);
    const image = s?.avatar_url ?? undefined;
    const meta: Array<Record<string, string>> = [
      { title },
      { name: "description", content: desc },
      { property: "og:title", content: title },
      { property: "og:description", content: desc },
      { property: "og:url", content: url },
      { property: "og:type", content: "profile" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: desc },
    ];
    if (image) {
      meta.push({ property: "og:image", content: image }, { name: "twitter:image", content: image });
    }
    if (!enabled || !s) {
      meta.push({ name: "robots", content: "noindex,follow" });
    }
    return {
      meta,
      links: [{ rel: "canonical", href: url }],
      scripts: enabled && s
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "ProfilePage",
                mainEntity: {
                  "@type": "Person",
                  name,
                  description: desc,
                  image: image ?? undefined,
                  url,
                },
              }),
            },
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Home", item: "https://bidnovaauctions.uk/" },
                  { "@type": "ListItem", position: 2, name: "Sellers", item: "https://bidnovaauctions.uk/auctions" },
                  { "@type": "ListItem", position: 3, name, item: url },
                ],
              }),
            },
          ]
        : [],
    };
  },
  component: SellerProfilePage,
  errorComponent: SellerError,
  notFoundComponent: SellerNotFound,
});

const CARD_COLS =
  "id,title,images,end_time,current_bid_pence,starting_price_pence,bid_count,grading_company,grade,seller_id,status,promoted_until,brand,product_line_set,compatible_game,card_set,card_number,card_language,finish,edition,trading_card_game,trading_card_game_other,rarity,rarity_other,is_autographed,is_serial_numbered,serial_number";

type SocialIconEntry = { key: string; url: string; label: string; Icon: typeof Globe };

function socialIcons(s: NonNullable<ReturnType<typeof buildSocials>>): SocialIconEntry[] {
  const list: SocialIconEntry[] = [];
  if (s.twitter) list.push({ key: "twitter", url: s.twitter, label: "Twitter / X", Icon: Twitter });
  if (s.instagram) list.push({ key: "instagram", url: s.instagram, label: "Instagram", Icon: Instagram });
  if (s.facebook) list.push({ key: "facebook", url: s.facebook, label: "Facebook", Icon: Facebook });
  if (s.youtube) list.push({ key: "youtube", url: s.youtube, label: "YouTube", Icon: Youtube });
  if (s.discord) list.push({ key: "discord", url: s.discord, label: "Discord", Icon: MessageCircle });
  return list;
}

function StatTile({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-border/60 bg-card/60 p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
        <Icon className="h-3.5 w-3.5 text-gold" aria-hidden />
        {label}
      </div>
      <div className="mt-1 font-display text-xl text-foreground">{value}</div>
    </div>
  );
}

function normaliseUrl(v: string | null | undefined): string | null {
  if (!v) return null;
  const trimmed = v.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function buildSocials(raw: unknown) {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, string | null | undefined>;
  return {
    twitter: normaliseUrl(r.twitter),
    instagram: normaliseUrl(r.instagram),
    facebook: normaliseUrl(r.facebook),
    youtube: normaliseUrl(r.youtube),
    tiktok: normaliseUrl(r.tiktok),
    discord: normaliseUrl(r.discord),
  };
}

function SellerProfilePage() {
  const { t } = useTranslation("marketing");
  const { sellerId } = Route.useParams();

  const { data: seller, isLoading: sellerLoading } = useQuery({
    queryKey: ["public-seller", sellerId],
    queryFn: () => fetchPublicSeller(sellerId),
  });

  const { data: allAuctions, isLoading: auctionsLoading } = useQuery({
    queryKey: ["seller-auctions", seller?.id ?? sellerId],
    enabled: !!seller?.id,
    queryFn: async (): Promise<AuctionCardData[]> => {
      const { data, error } = await supabase
        .from("auctions")
        .select(CARD_COLS)
        .eq("seller_id", seller!.id)
        .in("status", ["active", "ended"])
        .order("end_time", { ascending: false })
        .limit(60);
      if (error) throw error;
      return (data ?? []) as AuctionCardData[];
    },
  });

  if (sellerLoading) {
    return <div className="mx-auto max-w-6xl px-4 py-10 text-sm text-muted-foreground">{t("seller.loading", "Loading…")}</div>;
  }

  if (!seller || !seller.seller_profile_enabled) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-10">
        <h1 className="font-display text-3xl">{t("seller.profileUnavailableTitle", "Seller profile unavailable")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("seller.profileUnavailableBody", "This seller has not published a public profile yet.")}
        </p>
        <p className="mt-4 text-sm">
          <Link to="/auctions" className="text-gold underline">
            {t("seller.browseAllAuctions", "Browse all auctions")}
          </Link>
        </p>
      </div>
    );
  }

  const name = resolvePublicSellerName(seller);
  const initial = (name.trim()[0] ?? "S").toUpperCase();
  const joined = seller.joined_at
    ? new Date(seller.joined_at).toLocaleDateString(undefined, { month: "long", year: "numeric" })
    : null;
  const website = normaliseUrl(seller.website_url);
  const socials = buildSocials(seller.social_links);
  const socialList = socials ? socialIcons(socials) : [];
  const now = Date.now();
  const active = (allAuctions ?? []).filter(
    (a) => (a as { status?: string }).status === "active" && new Date(a.end_time).getTime() > now,
  );
  const ended = (allAuctions ?? []).filter(
    (a) => !active.includes(a),
  );
  const featured = active
    .filter((a) => a.promoted_until && new Date(a.promoted_until).getTime() > now)
    .slice(0, 6);

  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <section className="overflow-hidden rounded-2xl border border-border/60 bg-card gold-ring">
        <div
          className="relative h-40 w-full bg-gradient-to-br from-primary/30 via-black/40 to-gold/20 sm:h-56"
          style={
            seller.cover_image_url
              ? {
                  backgroundImage: `url(${seller.cover_image_url})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }
              : undefined
          }
        >
          <div className="absolute inset-0 bg-gradient-to-t from-card via-card/40 to-transparent" />
        </div>
        <div className="-mt-12 flex flex-col gap-5 p-6 sm:flex-row sm:items-end">
          <Avatar className="h-24 w-24 border-4 border-card shadow-xl">
            {seller.avatar_url && <AvatarImage src={seller.avatar_url} alt={name} />}
            <AvatarFallback className="text-2xl">{initial}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-display text-3xl">{name}</h1>
              <SellerBadgeStack sellerId={seller.id} size="md" />
            </div>
            {seller.username && (
              <p className="mt-1 text-sm text-muted-foreground">@{seller.username}</p>
            )}
            {seller.bio && <p className="mt-3 text-sm text-foreground/90 whitespace-pre-line">{seller.bio}</p>}
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <span
                className="inline-flex items-center gap-1.5"
                aria-label={
                  (seller.seller_rating_count ?? 0) > 0 && seller.seller_rating != null
                    ? `Average rating ${seller.seller_rating.toFixed(1)} out of 5 from ${seller.seller_rating_count} reviews`
                    : "No reviews yet"
                }
              >
                <Star className="h-4 w-4 text-gold" aria-hidden />
                {(seller.seller_rating_count ?? 0) > 0 && seller.seller_rating != null ? (
                  <>
                    <span className="text-foreground font-medium">{seller.seller_rating.toFixed(1)}</span>
                    <span>{t("seller.reviewCount", "{count, plural, one {(# review)} other {(# reviews)}}", { count: seller.seller_rating_count ?? 0 })}</span>
                  </>
                ) : (
                  <span>{t("seller.noReviewsYet", "No reviews yet")}</span>
                )}
              </span>
              {(seller.seller_rating_count ?? 0) > 0 && seller.seller_positive_pct != null && (
                <span className="inline-flex items-center gap-1.5">
                  <ThumbsUp className="h-4 w-4" aria-hidden />
                  {t("seller.positivePct", "{pct}% positive", { pct: Math.round(seller.seller_positive_pct) })}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5">
                <Package className="h-4 w-4" aria-hidden />
                {t("seller.completedSalesCount", "{count, plural, one {# completed sale} other {# completed sales}}", { count: seller.seller_sales_count ?? 0 })}
              </span>
              {joined && (
                <span className="inline-flex items-center gap-1.5">
                  <CalendarDays className="h-4 w-4" aria-hidden />
                  {t("seller.memberSince", "Member since {date}", { date: joined })}
                </span>
              )}
              {seller.location && (
                <span className="inline-flex items-center gap-1.5">📍 {seller.location}</span>
              )}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {website && (
                <a
                  href={website}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex items-center gap-1.5 rounded-full border border-border/60 px-3 py-1 text-xs text-foreground/90 hover:border-gold hover:text-gold"
                >
                  <Globe className="h-3.5 w-3.5" aria-hidden /> {t("seller.website", "Website")}
                </a>
              )}
              {socialList.map(({ key, url, label, Icon }) => (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  aria-label={label}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border/60 text-muted-foreground hover:border-gold hover:text-gold"
                >
                  <Icon className="h-4 w-4" aria-hidden />
                </a>
              ))}
              <ShareProfileButton
                url={`https://bidnovaauctions.uk/seller/${seller.username ?? seller.id}`}
                title={`${name} on BidNova Auctions`}
                text={seller.bio ?? undefined}
                className="ml-auto"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <StatTile icon={Gavel} label={t("seller.statTotalAuctions", "Total auctions")} value={String((allAuctions ?? []).length)} />
        <StatTile icon={Package} label={t("seller.statTotalSales", "Total sales")} value={String(seller.seller_sales_count ?? 0)} />
        <StatTile icon={Package} label={t("seller.statSuccessfulSales", "Successful sales")} value={String(seller.seller_sales_count ?? 0)} />
        <StatTile
          icon={Star}
          label={t("seller.statAverageFeedback", "Average feedback")}
          value={
            (seller.seller_rating_count ?? 0) > 0 && seller.seller_rating != null
              ? `${seller.seller_rating.toFixed(1)} / 5`
              : "—"
          }
        />
        <StatTile
          icon={ThumbsUp}
          label={t("seller.statPositiveFeedback", "Positive feedback")}
          value={
            (seller.seller_rating_count ?? 0) > 0 && seller.seller_positive_pct != null
              ? `${Math.round(seller.seller_positive_pct)}%`
              : "—"
          }
        />
        <StatTile
          icon={Award}
          label={t("seller.statVerification", "Verification")}
          value={seller.is_verified_seller ? t("seller.verified", "Verified") : t("seller.community", "Community")}
        />
      </section>

      {featured.length > 0 && (
        <section className="mt-10">
          <h2 className="font-display text-2xl">{t("seller.featuredProducts", "Featured products")}</h2>
          <div className="mt-4">
            <AuctionGrid auctions={featured} emptyText="" />
          </div>
        </section>
      )}

      <section className="mt-10">
        <h2 className="font-display text-2xl">{t("seller.activeAuctions", "Active auctions")}</h2>
        <div className="mt-4">
          {auctionsLoading ? (
            <div className="text-sm text-muted-foreground">{t("seller.loadingListings", "Loading listings…")}</div>
          ) : (
            <AuctionGrid auctions={active} emptyText={t("seller.noActiveListings", "This seller has no active listings.")} />
          )}
        </div>
      </section>

      {ended.length > 0 && (
        <section className="mt-10">
          <h2 className="font-display text-2xl">{t("seller.recentlyEnded", "Recently ended")}</h2>
          <div className="mt-4">
            <AuctionGrid auctions={ended.slice(0, 12)} emptyText="" />
          </div>
        </section>
      )}

      <section className="mt-10">
        <h2 className="font-display text-2xl">{t("seller.recentReviews", "Recent reviews")}</h2>
        <div className="mt-4">
          <SellerReviewsSection sellerId={seller.id} />
        </div>
      </section>
    </div>
  );
}


function SellerError() {
  const { t } = useTranslation("marketing");
  const router = useRouter();
  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <h1 className="font-display text-2xl">{t("seller.somethingWentWrong", "Something went wrong")}</h1>
      <button className="mt-4 text-sm text-gold underline" onClick={() => router.invalidate()}>
        {t("seller.tryAgain", "Try again")}
      </button>
    </div>
  );
}

function SellerNotFound() {
  const { t } = useTranslation("marketing");
  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <h1 className="font-display text-3xl">{t("seller.notFoundTitle", "Seller not found")}</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        <Link to="/" className="text-gold underline">
          {t("seller.returnHome", "Return home")}
        </Link>
      </p>
    </div>
  );
}
